import {
  editObjectData,
  editRowData
} from "@aclymatepackages/array-immutability-helpers";

import { transactionsQuestions } from "./helpers";

const parseFieldId = (fieldId) => {
  if (typeof fieldId !== "string") {
    return { prefix: null, rest: [] };
  }
  const [prefix, ...rest] = fieldId.split(":");
  return { prefix, rest };
};

const handleVendorUpdate = ({ rest, value, context }) => {
  const [key] = rest;
  if (!key) {
    return false;
  }
  const { setVendorData, selectedYear } = context;

  if (key === "revenueYears") {
    const { vendorData } = context;
    const existing = (vendorData?.revenueYears || []).filter(
      ({ year }) => Number(year) !== Number(selectedYear)
    );
    editObjectData(setVendorData, "revenueYears", [
      ...existing,
      { year: selectedYear, revenue: value }
    ]);
    return true;
  }

  if (key === "employeeCount") {
    const { vendorData } = context;
    editObjectData(setVendorData, "employeeCount", value);
    editObjectData(setVendorData, "yearlyEmployeeCounts", {
      ...(vendorData?.yearlyEmployeeCounts || {}),
      [selectedYear]: value
    });
    return true;
  }

  if (key === "industry") {
    const { vendorData } = context;
    editObjectData(setVendorData, "industry", value);
    editObjectData(setVendorData, "yearlyIndustry", {
      ...(vendorData?.yearlyIndustry || {}),
      [selectedYear]: value
    });
    return true;
  }

  editObjectData(setVendorData, key, value);
  return true;
};

const handleFacilityUpdate = ({ rest, value, context }) => {
  const [idxString, key] = rest;
  const idx = Number(idxString);
  if (!key || Number.isNaN(idx)) {
    return false;
  }
  const facilities = context.facilities || [];
  if (idx < 0 || idx >= facilities.length) {
    return false;
  }
  editRowData(idx, context.setFacilities)(key, value);
  return true;
};

const findTransactionCategoryDefaultNaics = (scopeThreeCategory) => {
  const category = transactionsQuestions.find(
    (q) => q.scopeThreeCategory === scopeThreeCategory
  );
  return category?.naics ? String(category.naics) : null;
};

const findTransactionArrayIdx = (scopeThreeCategory) =>
  transactionsQuestions.findIndex(
    (q) => q.scopeThreeCategory === scopeThreeCategory
  );

const handleTransactionsUpdate = ({ rest, value, context }) => {
  const [scopeThreeCategoryString] = rest;
  const scopeThreeCategory = Number(scopeThreeCategoryString);
  if (Number.isNaN(scopeThreeCategory)) {
    return false;
  }
  const targetIdx = findTransactionArrayIdx(scopeThreeCategory);
  if (targetIdx < 0) {
    return false;
  }
  if (value && typeof value === "object" && !Array.isArray(value)) {
    Object.entries(value).map(([naics, amount]) =>
      editRowData(targetIdx, context.setTransactions)(naics, amount)
    );
    return true;
  }
  const numericValue = Number(value);
  if (!Number.isNaN(numericValue) && numericValue >= 0) {
    const defaultNaics =
      findTransactionCategoryDefaultNaics(scopeThreeCategory);
    if (!defaultNaics) {
      return false;
    }
    editRowData(targetIdx, context.setTransactions)(defaultNaics, numericValue);
    return true;
  }
  return false;
};

const handleVehicleFuelUpdate = ({ rest, value, context }) => {
  const [key] = rest;
  if (!key) {
    return false;
  }
  editObjectData(context.setVehicleFuelData, key, value);
  return true;
};

const handleSurveyUpdate = ({ rest, value, context }) => {
  const [key] = rest;
  if (key !== "year") {
    return false;
  }
  const year = Number(value);
  if (Number.isNaN(year)) {
    return false;
  }
  const availableYears = context.availableYears || [];
  if (availableYears.length && !availableYears.includes(year)) {
    return false;
  }
  context.setSelectedYear(year);
  return true;
};

const handleScopeTonsUpdate = ({ rest, value, context }) => {
  const [scopeString] = rest;
  const scope = Number(scopeString);
  if (Number.isNaN(scope)) {
    return false;
  }
  const { scopeTons, setScopeTons } = context;
  const idx = (scopeTons || []).findIndex((row) => row.scope === scope);
  if (idx < 0) {
    return false;
  }
  editRowData(idx, setScopeTons)("tons", value);
  return true;
};

const FIELD_HANDLERS = {
  vendor: handleVendorUpdate,
  facility: handleFacilityUpdate,
  transactions: handleTransactionsUpdate,
  "vehicle-fuel": handleVehicleFuelUpdate,
  "scope-tons": handleScopeTonsUpdate,
  survey: handleSurveyUpdate
};

const applyFieldUpdate = ({ fieldUpdate, context }) => {
  if (!fieldUpdate || !fieldUpdate.fieldId) {
    return false;
  }
  const { prefix, rest } = parseFieldId(fieldUpdate.fieldId);
  const handler = FIELD_HANDLERS[prefix];
  if (!handler) {
    return false;
  }
  return handler({ rest, value: fieldUpdate.value, context });
};

const isMeaningfulValue = (value) =>
  value !== undefined && value !== null && value !== "";

const hasExistingValue = ({ fieldId, context }) => {
  const { prefix, rest } = parseFieldId(fieldId);
  if (prefix === "vendor") {
    const [key] = rest;
    return isMeaningfulValue(context.vendorData?.[key]);
  }
  if (prefix === "facility") {
    const [idxString, key] = rest;
    const idx = Number(idxString);
    const facility = context.facilities?.[idx];
    if (!facility) {
      return false;
    }
    if (key) {
      return isMeaningfulValue(facility[key]);
    }
    return Object.values(facility).some(isMeaningfulValue);
  }
  if (prefix === "transactions") {
    const [scopeThreeCategoryString] = rest;
    const idx = findTransactionArrayIdx(Number(scopeThreeCategoryString));
    if (idx < 0) {
      return false;
    }
    const row = context.transactions?.[idx];
    return !!row && Object.values(row).some((value) => Number(value) > 0);
  }
  if (prefix === "vehicle-fuel") {
    const [key] = rest;
    if (key && key !== "all") {
      return Number(context.vehicleFuelData?.[key]) > 0;
    }
    const data = context.vehicleFuelData || {};
    return Object.values(data).some((value) => Number(value) > 0);
  }
  if (prefix === "scope-tons") {
    const [scopeString] = rest;
    if (scopeString === "all" || !scopeString) {
      return (context.scopeTons || []).some(
        (row) => Number(row?.tons) > 0 || row?.unknown
      );
    }
    const scope = Number(scopeString);
    const row = (context.scopeTons || []).find((r) => r.scope === scope);
    return Number(row?.tons) > 0;
  }
  if (prefix === "survey") {
    const [key] = rest;
    if (key === "year") {
      return !!context.selectedYear;
    }
    return false;
  }
  return false;
};

const formatRevenue = (vendorData, year) => {
  const entry = (vendorData?.revenueYears || []).find(
    ({ year: y }) => Number(y) === Number(year)
  );
  if (!entry?.revenue) {
    return null;
  }
  return `$${Number(entry.revenue).toLocaleString()}`;
};

const formatIndustry = (vendorData, year) => {
  const industry = vendorData?.yearlyIndustry?.[year] || vendorData?.industry;
  if (!industry) {
    return null;
  }
  if (typeof industry === "string") {
    return industry;
  }
  return industry.label || industry.title || industry.description || null;
};

const formatEmployeeCount = (vendorData, year) => {
  const count =
    vendorData?.yearlyEmployeeCounts?.[year] ?? vendorData?.employeeCount;
  if (count === undefined || count === null || count === "") {
    return null;
  }
  return `${count}`;
};

const formatScopeTons = (scopeTons) => {
  const filled = (scopeTons || []).filter(
    (row) => Number(row?.tons) > 0 || row?.unknown
  );
  if (!filled.length) {
    return null;
  }
  return filled
    .map((row) =>
      row.unknown ? `S${row.scope}: unknown` : `S${row.scope}: ${row.tons}t`
    )
    .join(" · ");
};

const formatFacility = (facility) => {
  if (!facility) {
    return null;
  }
  const name = facility.name;
  const addressDescription = facility.address?.description;
  const sqft = facility.sqFootage ? Number(facility.sqFootage) : null;
  if (!name || !addressDescription || !sqft) {
    return null;
  }
  return `${name} (${sqft} sq ft)`;
};

const formatVehicleFuel = (vehicleFuelData) => {
  if (!vehicleFuelData) {
    return null;
  }
  if (Number(vehicleFuelData.fuelVolume) > 0) {
    const unit = vehicleFuelData.fuelVolumeUnit || "gallons";
    return `${Math.round(vehicleFuelData.fuelVolume)} ${unit}`;
  }
  if (Number(vehicleFuelData.fuelDollars) > 0) {
    return `$${Number(vehicleFuelData.fuelDollars).toLocaleString()}`;
  }
  return null;
};

const formatTransactionTotal = (transactionRow) => {
  if (!transactionRow) {
    return null;
  }
  const total = Object.values(transactionRow).reduce(
    (sum, value) => sum + (Number(value) || 0),
    0
  );
  return total > 0 ? `$${total.toLocaleString()}` : null;
};

const FIELD_FORMATTERS = {
  "survey:year": ({ selectedYear }) =>
    selectedYear ? String(selectedYear) : null,
  "vendor:industry": ({ vendorData, selectedYear }) =>
    formatIndustry(vendorData, selectedYear),
  "vendor:revenueYears": ({ vendorData, selectedYear }) =>
    formatRevenue(vendorData, selectedYear),
  "vendor:employeeCount": ({ vendorData, selectedYear }) =>
    formatEmployeeCount(vendorData, selectedYear),
  "vendor:isRemote": ({ vendorData }) => {
    if (vendorData?.isRemote === undefined) {
      return null;
    }
    return vendorData.isRemote ? "Fully remote" : "Has offices";
  },
  "vendor:address": ({ vendorData }) =>
    vendorData?.address?.description || null,
  "vendor:hasNoCompanyVehicles": ({ vendorData }) => {
    if (vendorData?.hasNoCompanyVehicles === undefined) {
      return null;
    }
    return vendorData.hasNoCompanyVehicles
      ? "No company vehicles"
      : "Has company vehicles";
  },
  "scope-tons:all": ({ scopeTons }) => formatScopeTons(scopeTons),
  "vehicle-fuel:all": ({ vehicleFuelData }) =>
    formatVehicleFuel(vehicleFuelData)
};

const getFieldDisplayValue = (fieldId, context) => {
  if (!fieldId) {
    return null;
  }
  const exactFormatter = FIELD_FORMATTERS[fieldId];
  if (exactFormatter) {
    return exactFormatter(context);
  }
  if (fieldId.startsWith("facility:")) {
    const idx = Number(fieldId.split(":")[1]);
    return formatFacility(context.facilities?.[idx]);
  }
  if (fieldId.startsWith("transactions:")) {
    const scopeThree = Number(fieldId.split(":")[1]);
    const idx = findTransactionArrayIdx(scopeThree);
    if (idx < 0) {
      return null;
    }
    return formatTransactionTotal(context.transactions?.[idx]);
  }
  return null;
};

const FIELD_CONFIRM_PROMPTS = {
  "survey:year": ({ selectedYear }) => {
    if (!selectedYear) {
      return null;
    }
    return `Let's pick up where we left off — are we working on ${selectedYear}?`;
  },
  "vendor:isRemote": ({ vendorData }) => {
    if (vendorData?.isRemote === undefined) {
      return null;
    }
    return vendorData.isRemote
      ? "Last time you told us your company is fully remote — is that still the case?"
      : "Last time you told us your company has physical facilities — is that still the case?";
  },
  "vendor:hasNoCompanyVehicles": ({ vendorData }) => {
    if (vendorData?.hasNoCompanyVehicles === undefined) {
      return null;
    }
    return vendorData.hasNoCompanyVehicles
      ? "Last time you told us your company doesn't own any vehicles — is that still the case?"
      : "Last time you told us your company owns vehicles — is that still the case?";
  }
};

const buildFacilityConfirmPrompt = (fieldId, context) => {
  const idx = Number(fieldId.split(":")[1]);
  const facility = context.facilities?.[idx];
  const display = formatFacility(facility);
  if (!display) {
    return null;
  }
  const addressDescription = facility?.address?.description;
  const location = addressDescription ? ` at ${addressDescription}` : "";
  return `I've got **${display}**${location} on file — still accurate?`;
};

const getFieldConfirmPrompt = (fieldId, context) => {
  if (!fieldId) {
    return null;
  }
  const builder = FIELD_CONFIRM_PROMPTS[fieldId];
  if (builder) {
    return builder(context);
  }
  if (fieldId.startsWith("facility:")) {
    return buildFacilityConfirmPrompt(fieldId, context);
  }
  return null;
};

export {
  applyFieldUpdate,
  hasExistingValue,
  getFieldDisplayValue,
  getFieldConfirmPrompt,
  FIELD_HANDLERS
};
